import { Box, Button, Checkbox, Grid, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Footer } from 'src/components/footer/Footer';
import { NoResultAlert } from 'src/components/noResultAlert';
import { Pagination } from 'src/components/pagination/Pagination';
import { Progress } from 'src/components/progress/Progress';
import { CustomTooltip } from 'src/components/tooltip/CustomTooltip';
import { PATHS } from 'src/constants/paths';
import { lang } from 'src/utils';

import { useAddLocations } from './AddLocations.hook';
import { AddLocationsModalProps } from './AddLocations.interface';
import {
  EmptyLocation,
  NoResultContainer,
  TableCheckboxLabel,
  TableContainer,
  TableGrid,
  TableHeader,
} from './AddLocations.styled';

export const AddLocations = (props: AddLocationsModalProps) => {
  const { locationGroupId = '' } = useParams();
  const navigate = useNavigate();

  const {
    canSave,
    formMethods,
    isPaginatedLocationsLoading,
    onChangedSelectedLocations,
    onClickSelectAll,
    selectedLocations,
    totalPages,
    totalCount,
    page,
    handlePageChange,
    activeFilters,
    pageSize,
  } = useAddLocations(locationGroupId, props.channelName ?? '');
  const [isSaving, setIsSaving] = useState(false);

  const start = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalCount);

  const onClickedSave = async () => {
    setIsSaving(true);
    await props.onSave(selectedLocations);
    setIsSaving(false);
  };

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}>
        {formMethods.watch().locations.length !== 0 && (
          <>
            <Typography variant="body1" color="grey">
              {`${lang.Showing} ${start}–${end} of ${totalCount}`}
            </Typography>
            <Button
              variant="text"
              onClick={onClickSelectAll}
              disabled={formMethods.watch().locations.length === 0}>
              {formMethods
                .watch()
                .locations.every((loc) =>
                  selectedLocations.some(
                    (sel) => sel.location_code === loc.location_code,
                  ),
                )
                ? lang.deselectAll
                : lang.selectAll}
            </Button>
          </>
        )}
      </Box>
      <TableContainer>
        <TableHeader>
          <Typography variant="h6" component="h2">
            {lang.selectLocations}
          </Typography>
        </TableHeader>
        {isPaginatedLocationsLoading ? (
          <Progress data-testid="progress" />
        ) : formMethods.watch().locations.length === 0 &&
          isEmpty(activeFilters) ? (
          <EmptyLocation>{lang.noMatchingLocation}</EmptyLocation>
        ) : formMethods.watch().locations.length === 0 ? (
          <NoResultContainer data-testid="no-result-alert">
            <NoResultAlert />
          </NoResultContainer>
        ) : (
          <TableGrid container spacing={1}>
            {formMethods
              .watch()
              .locations.map(
                (location: import('src/interfaces').ILocationListing) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={4}
                    xl={4}
                    key={location.location_code}>
                    <TableCheckboxLabel
                      control={
                        <Checkbox
                          checked={selectedLocations.some(
                            (sel) =>
                              sel.location_code === location.location_code,
                          )}
                          onChange={() => onChangedSelectedLocations(location)}
                        />
                      }
                      label={
                        <CustomTooltip
                          width={400}
                          placement="top"
                          overflowWidth={300}
                          title={`${location.store_number ?? ''} - ${
                            location.name ?? ''
                          }`}>
                          <p>
                            {`${location.store_number ?? ''} - ${
                              location.name ?? ''
                            }`}
                          </p>
                        </CustomTooltip>
                      }
                    />
                  </Grid>
                ),
              )}
          </TableGrid>
        )}
      </TableContainer>
      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination
          totalPages={totalPages}
          page={page}
          onChange={handlePageChange}
        />
      </Box>
      <Footer
        data-testid="footer"
        showFooterActions
        hasSubNav
        onCancel={() =>
          navigate({
            pathname: PATHS.LOCATION_GROUPS,
          })
        }
        onSave={onClickedSave}
        saveLabel={lang.addLocations}
        cancelLabel={lang.cancel}
        disableSave={!canSave}
        isSaving={isSaving}
      />
    </>
  );
};
